import { inject, Injectable, DOCUMENT } from "@angular/core";
import { updatePreset as _updatePreset, palette } from "@primeuix/themes";
import { Colors } from "../models";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  #document = inject(DOCUMENT);

  private setThemeVariables(theme: Record<string, string>, prefix = "--primary", mode: string): void {
    const themeEl = this.#document.querySelector(`.${mode}`) as HTMLElement;

    if (!themeEl) {
      console.error("Theme element not found");
      return;
    }

    Object.entries(theme).forEach(([key, value]) => {
      themeEl.style.setProperty(`${prefix}-${key}`, value);
    });
  }

  updatePreset(colors: Colors | null, mode: string) {
    if (!colors) {
      console.error("Theme colors not found");
      return;
    }
    const shades = palette(colors.primaryColor ?? "");
    const secondaryShades = palette(colors.secondaryColor ?? "");
    const theme: { [key: string]: string } = {
      "forms-text-color": colors.primaryTextColor!,
      "forms-menu-bg": colors.menuBackground!,
      "forms-menu-text-color": colors.menuTextColor!,
      "forms-btn-text-color": colors.primaryBtnTextColor!,
      "forms-secondary-btn-text-color": colors.secondaryBtnTextColor!,
      "forms-primary-color": colors.primaryColor!,
      "forms-secondary-color": colors.secondaryColor!,
      "forms-table-primary-bg": colors.tablePrimaryBackground!,
      "forms-table-secondary-bg": colors.tableSecondaryBackground!,
      "forms-bg": colors.bodyBackground!,
      "forms-header-bg": colors.headerBackground!,
      "forms-header-text-color": colors.headerText!,
      "forms-success": colors.success ?? "var(--success)",
    };

    this.setThemeVariables({ ...(shades as Record<string, string>), ...theme }, "--primary", mode);
    this.setThemeVariables(secondaryShades as Record<string, string>, "--secondary", mode);

    _updatePreset({
      primitive: {
        forms: {
          0: "var(--primary-0)",
          50: "var(--primary-50)",
          100: "var(--primary-100)",
          200: "var(--primary-200)",
          300: "var(--primary-300)",
          400: "var(--primary-400)",
          500: "var(--primary-500)",
          600: "var(--primary-600)",
          700: "var(--primary-700)",
          800: "var(--primary-800)",
          900: "var(--primary-900)",
          950: "var(--primary-950)",
          textColor: "var(--primary-forms-text-color)",
          // Let the primary shades assign colors to components
          // However, still adding the following colors from config api just in case requirements change
          menuBackground: "var(--primary-forms-menu-bg)",
          menuTextColor: "var(--primary-forms-menu-text-color)",
          primaryBtnTextColor: "var(--primary-forms-btn-text-color)",
          secondaryBtnTextColor: "var(--primary-forms-secondary-btn-text-color)",
          secondaryColor: "var(--primary-forms-secondary-color)",
          tablePrimaryBackground: "var(--primary-forms-table-primary-bg)",
          tableSecondaryBackground: "var(--primary-forms-table-secondary-bg)",
          bodyBackground: "var(--primary-forms-bg)",
          secondary0: "var(--secondary-0)",
          secondary50: "var(--secondary-50)",
          secondary100: "var(--secondary-100)",
          secondary200: "var(--secondary-200)",
          secondary300: "var(--secondary-300)",
          secondary400: "var(--secondary-400)",
          secondary500: "var(--secondary-500)",
          secondary600: "var(--secondary-600)",
          secondary700: "var(--secondary-700)",
          secondary800: "var(--secondary-800)",
          secondary900: "var(--secondary-900)",
          secondary950: "var(--secondary-950)",
        },
      },
      semantic: {
        primary: {
          0: "{forms.0}",
          50: "{forms.50}",
          100: "{forms.100}",
          200: "{forms.200}",
          300: "{forms.300}",
          400: "{forms.400}",
          500: "{forms.500}",
          600: "{forms.600}",
          700: "{forms.700}",
          800: "{forms.800}",
          900: "{forms.900}",
          950: "{forms.950}",
        },
        colorScheme: {
          light: {
            text: {
              color: "{forms.textColor}",
            },
          },
          dark: {
            text: {
              color: "{forms.textColor}",
            },
            surface: {
              0: "{neutral.0}",
              50: "{neutral.50}",
              100: "{neutral.100}",
              200: "{neutral.200}",
              300: "{neutral.300}",
              400: "{neutral.400}",
              500: "{neutral.500}",
              600: "{neutral.600}",
              700: "{neutral.700}",
              800: "{neutral.800}",
              900: "{neutral.900}",
              950: "{neutral.950}",
            },
          },
        },
        secondary: {
          0: "{forms.secondary0}",
          50: "{forms.secondary50}",
          100: "{forms.secondary100}",
          200: "{forms.secondary200}",
          300: "{forms.secondary300}",
          400: "{forms.secondary400}",
          500: "{forms.secondary500}",
          600: "{forms.secondary600}",
          700: "{forms.secondary700}",
          800: "{forms.secondary800}",
          900: "{forms.secondary900}",
          950: "{forms.secondary950}",
        },
      },
      components: {
        button: {
          colorScheme: {
            light: {
              root: {
                primary: {},
                secondary: {
                  background: "{forms.secondary500}",
                  borderColor: "{forms.secondary500}",
                  hoverBackground: "{forms.secondary600}",
                  hoverBorderColor: "{forms.secondary600}",
                  activeBackground: "{forms.secondary700}",
                  activeBorderColor: "{forms.secondary700}",
                  color: "{forms.secondaryBtnTextColor}",
                },
              },
              outlined: {
                primary: {},
                secondary: {
                  borderColor: "{forms.secondary500}",
                  hoverBackground: "{forms.secondary50}",
                  activeBackground: "{forms.secondary500}",
                  color: "{forms.secondaryBtnTextColor}",
                },
              },
              text: {
                primary: {
                  color: "{forms.primaryTextColor}",
                },
              },
            },
            dark: {
              root: {
                primary: {},
                secondary: {
                  background: "{forms.secondary500}",
                  borderColor: "{forms.secondary500}",
                  hoverBackground: "{forms.secondary600}",
                  activeBackground: "{forms.secondary700}",
                  color: "{forms.secondaryBtnTextColor}",
                },
              },
              outlined: {
                primary: {},
                secondary: {
                  borderColor: "{forms.secondary500}",
                  hoverBackground: "{forms.secondary50}",
                  activeBackground: "{forms.secondary500}",
                  color: "{forms.secondaryBtnTextColor}",
                },
              },
            },
          },
        },
        checkbox: {
          root: {
            checkedBackground: "{forms.secondary500}",
            checkedHoverBackground: "{forms.secondary500}",
          },
        },
        radiobutton: {
          root: {
            checkedBackground: "{forms.secondary500}",
            checkedHoverBackground: "{forms.secondary500}",
          },
        },
      },
    });
  }
}
